import i18n from 'i18next';
import * as Yup from 'yup';
import { parseInstant, dayjs } from '@koob/dates';

export const getCancelConditionsSchema = (showNumberOfNights = true) =>
  Yup.array()
    .of(
      Yup.object().shape({
        deduction: Yup.number()
          .typeError(i18n.t('errors:required'))
          .required(i18n.t('errors:required'))
          .min(0, i18n.t('hotelRoom:isPositive')),
        daysFrom: Yup.number()
          .required(i18n.t('errors:required'))
          .min(0, i18n.t('hotelRoom:isPositive')),
        daysTo: Yup.number()
          .required(i18n.t('errors:required'))
          .min(0, i18n.t('hotelRoom:isPositive'))
          .test(
            'is-greater',
            i18n.t('hotelRoom:isGreatThanError'),
            function (value) {
              return value > this.parent.daysFrom;
            },
          ),
        numberOfNight: showNumberOfNights
          ? Yup.number().required(i18n.t('errors:required')).min(0)
          : Yup.number().nullable().notRequired(),
        period: Yup.array().required(i18n.t('errors:required')),
      }),
    )
    .test(
      'no-overlap',
      i18n.t('errors:cancelConditionsOverlap'),
      function (conditions) {
        if (!conditions || conditions.length <= 1) return true;

        // Build array of valid conditions with parsed data
        const validConditions = [];
        conditions.forEach((condition, index) => {
          if (!condition.period || condition.period.length < 2) return;
          if (
            condition.daysFrom === undefined ||
            condition.daysFrom === null ||
            condition.daysTo === undefined ||
            condition.daysTo === null
          )
            return;

          // Extract dates only (ignore time)
          const periodStart = dayjs(condition.period[0]).startOf('day');
          const periodEnd = dayjs(condition.period[1]).startOf('day');

          validConditions.push({
            index,
            periodStart,
            periodEnd,
            daysFrom: Number(condition.daysFrom),
            daysTo: Number(condition.daysTo),
          });
        });

        // Check each pair of conditions
        for (let i = 0; i < validConditions.length; i++) {
          for (let j = i + 1; j < validConditions.length; j++) {
            const condA = validConditions[i];
            const condB = validConditions[j];

            // Check if periods overlap (comparing dates only)
            // Two date ranges overlap if max(start1, start2) <= min(end1, end2)
            const periodOverlapStart = dayjs.max(
              condA.periodStart,
              condB.periodStart,
            );
            const periodOverlapEnd = dayjs.min(
              condA.periodEnd,
              condB.periodEnd,
            );
            const periodsOverlap =
              periodOverlapStart.isSameOrBefore(periodOverlapEnd);

            if (periodsOverlap) {
              // Check if day ranges overlap
              // Two ranges [a,b] and [c,d] overlap if max(a,c) <= min(b,d)
              const dayOverlapStart = Math.max(condA.daysFrom, condB.daysFrom);
              const dayOverlapEnd = Math.min(condA.daysTo, condB.daysTo);
              const dayRangesOverlap = dayOverlapStart <= dayOverlapEnd;

              if (dayRangesOverlap) {
                return this.createError({
                  path: `${this.path}[${condB.index}].daysTo`,
                  message: i18n.t('errors:cancelConditionsOverlap'),
                });
              }
            }
          }
        }
        return true;
      },
    );

export const formatCancellationPoliciesToForm = (
  cancellationPolicies,
  timezone = 'UTC',
) =>
  cancellationPolicies?.map((item) => ({
    id: item.id,
    period: [dayjs(item.startAt).tz(timezone), dayjs(item.endAt).tz(timezone)],
    numberOfNight: item.numberOfNight ?? 0,
    daysFrom: item.daysFrom,
    daysTo: item.daysTo,
    isPercent: item.isPercent ?? false,
    deduction: item.deduction,
  })) ?? [];

export const formatCancellationPoliciesToPayload = (values) =>
  values?.map((item) => ({
    id: item?.id,
    startAt: parseInstant(item.period?.[0])?.toISOString(),
    endAt: parseInstant(item.period?.[1])?.toISOString(),
    daysFrom: Number(item.daysFrom),
    daysTo: Number(item.daysTo),
    isPercent: item.isPercent ?? false,
    deduction: Number(item.deduction),
  })) ?? [];

export const getDefaultCondition = (timezone = 'UTC') => {
  const today = dayjs().tz(timezone).hour(12).minute(0).second(0);
  const tomorrow = dayjs(today).add(1, 'day').hour(11).minute(59).second(59);

  return {
    period: [today, tomorrow],
    numberOfNight: 0,
    daysFrom: 0,
    daysTo: 0,
    deduction: '',
    isPercent: false,
  };
};
